import type { FC } from 'react';
import React, { useContext, useCallback } from 'react';

import Button from '@atlaskit/button/custom-theme-button';

import {
	DISCOVER_ITEM_ON_HOME_PAGE_CLICK,
	setViewTransitionSource,
} from '@confluence/browser-metrics';
import { useRouteActions } from '@confluence/route-manager';
import { generatePathWithFeatureCodesQueryParam, FeatureCode } from '@confluence/feature-codes';
import { fg } from '@confluence/feature-gating';

import { FeedCardContext } from './FeedCardContext';
import type { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../../Feed';

const fullSizeButtonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			position: 'absolute',
			top: '0px',
			left: '0px',
			width: '100%',
			height: '100%',
		},
		spinnerStyles,
	};
};

// It is invalid (and not accessible) to put an html button/link inside another button.
// so to make the full card clickable without nesting buttons, we style it absolute and full size.
type Props = {
	href: string;
	contentType?: string;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
	followingFeedSortBy?: FollowingFeedSortByValue;
};
export const FeedCardInvisibleFullSizedLink: FC<Props> = ({
	href,
	contentType,
	popularFeedTimeGranularity,
	followingFeedSortBy,
}) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);
	const { push } = useRouteActions();

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
			if (contentType && contentType === 'page') {
				setViewTransitionSource(DISCOVER_ITEM_ON_HOME_PAGE_CLICK);
			}
			fireAnalyticsEvent({
				type: 'sendUIEvent',
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'feedCard',
				includeNavdex: true,
				additionalAttributes: {
					popularFeedTimeGranularity,
					followingFeedSortBy,
				},
			});

			if (fg('confluence_frontend_insights_feature_attribution')) {
				e.preventDefault();
				if (e.metaKey || e.ctrlKey) {
					window.open(generatePathWithFeatureCodesQueryParam(href, [FeatureCode.HOME_FEED]));
				} else {
					push(generatePathWithFeatureCodesQueryParam(href, [FeatureCode.HOME_FEED]));
				}
			}
		},
		[contentType, fireAnalyticsEvent, popularFeedTimeGranularity, followingFeedSortBy, href, push],
	);

	return (
		<Button
			href={href}
			appearance="link"
			theme={fullSizeButtonTheme}
			onClick={handleClick}
			testId="feed-card-body-link"
			aria-hidden
			tabIndex={-1}
		/>
	);
};
